import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackPurchase } from '../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();
        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Meta Pixel: Purchase solo per un abbonamento pagato (Standard, Premium, Founder Club), importo reale da Stripe
          if (['standard', 'premium', 'fondatori', 'founder'].includes(data.metadata?.plan) && data.amount_total != null) {
            trackPurchase(sessionId, data.amount_total / 100, (data.currency || 'eur').toUpperCase());
          }
          // Auto-redirect alla dashboard dopo 2 secondi
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }

        // If still pending, continue polling
        if (pollAttempts < maxAttempts) {
          setPollAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          // Timeout but might still be processing
          setStatus('success'); // Assume success after timeout
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollAttempts < maxAttempts) {
          setPollAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  const getPlanName = () => {
    if (paymentInfo?.metadata?.plan === 'premium') return 'Premium';
    if (paymentInfo?.metadata?.plan === 'standard') return 'Standard';
    if (['fondatori', 'founder'].includes(paymentInfo?.metadata?.plan)) return 'Founder Club';
    return 'il tuo piano';
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Loading State */}
        {status === 'loading' && (
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#4A7C59] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C3E50] mb-3">
              Verifica pagamento in corso...
            </h1>
            <p className="text-[#64748B]">
              Stiamo verificando il tuo pagamento. Attendi qualche secondo.
            </p>
            <div className="mt-6 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i} 
                  className={`w-2 h-2 rounded-full ${i <= pollAttempts ? 'bg-[#4A7C59]' : 'bg-gray-200'}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle2 className="w-10 h-10 text-[#4A7C59]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C3E50] mb-3">
              Pagamento completato!
            </h1>
            <p className="text-[#64748B] mb-2">
              Grazie per aver scelto CuraCheck {getPlanName()}!
            </p>
            <p className="text-[#64748B] text-sm mb-6">
              Il tuo abbonamento è ora attivo e puoi accedere a tutte le funzionalità.
            </p>
            
            {paymentInfo && (
              <div className="bg-[#FAF9F6] rounded-2xl p-4 mb-6 text-left">
                <h3 className="font-semibold text-[#2C3E50] mb-2">Dettagli acquisto:</h3>
                <div className="space-y-1 text-sm text-[#64748B]">
                  <p>Piano: <span className="font-medium text-[#4A7C59]">{getPlanName()}</span></p>
                  <p>Importo: <span className="font-medium">€{(paymentInfo.amount_total / 100).toFixed(2)}</span></p>
                </div>
              </div>
            )}

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white py-4 rounded-xl font-bold"
              data-testid="go-to-dashboard"
            >
              Vai alla Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C3E50] mb-3">
              Ops! Qualcosa è andato storto
            </h1>
            <p className="text-[#64748B] mb-6">
              Non siamo riusciti a verificare il pagamento. Se hai completato il pagamento, 
              contattaci e ti aiuteremo.
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/#prezzi')}
                className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white py-4 rounded-xl font-bold"
              >
                Riprova
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full border-[#4A7C59] text-[#4A7C59] py-4 rounded-xl font-bold"
              >
                Vai alla Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
